'use client';

import cn from 'classnames';
import { CSSProperties, useState } from 'react';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { type Cols, type ColBreakpoints } from '@/ui-components/atoms/Layout/Grid';
import { Text } from '@/ui-components/atoms/Text/Text';
import { SafeHtml } from '@/components/atoms/Display';
import { LinkWrapperIfHref } from '@/ui-components/atoms/LinkWrapperIfHref/LinkWrapperIfHref';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { ContentSamplingPlayer } from '@/ui-components/molecules/ContentSamplingPlayer/ContentSamplingPlayer';
import type { SamplingModalProps } from '@/ui-components/molecules/ContentSamplingPlayer/ContentSamplingPlayer';
import type { LinkProps } from '@/ui-components/atoms/Link/Link';
import styles from './contentsampling.module.scss';

export interface ContentSamplingItem {
    headline?: string | React.ReactNode;
    body?: React.ReactNode;
    link?: Link;
    audio?: {
        source?: string;
        transcript?: string;
        transcriptHeadline?: string;
        name?: string;
    };
    isLightTheme?: boolean;
    foregroundImage: {
        desktop?: Partial<ImageProps>;
        mobile?: Partial<ImageProps>;
    };
}

export interface ContentSamplingSection {
    desktopBackgroundUrl?: string;
    mobileBackgroundUrl?: string;
    backgroundColor?: string;
    isLightTheme?: boolean;
    items: ContentSamplingItem[];
}

interface Props {
    sections: ContentSamplingSection[];
    isReversed?: boolean;
}

interface GridProps {
    col: ColBreakpoints;
    order?: ColBreakpoints;
}

type ColumnType = 'media' | 'text';
type ColumnProps = Record<ColumnType, GridProps>;

function isEven(n: number): boolean {
    return (n + 1) % 2 === 0;
}

function calculateOrder(isReversed: boolean, i: number, isMedia: boolean): Cols {
    const even = isEven(i);
    if (isReversed) return isMedia ? (even ? 1 : 2) : even ? 2 : 1;
    return isMedia ? (even ? 2 : 1) : even ? 1 : 2;
}

function toLinkProps(link?: Link): LinkProps | undefined {
    if (!link?.href) return undefined;
    return { ...link, target: link.link_target } as LinkProps;
}

function MediaImage({
    image,
    link,
}: {
    image: {
        desktop?: Partial<ImageProps>;
        mobile?: Partial<ImageProps>;
    };
    link?: Link;
}): React.JSX.Element {
    const linkProps = toLinkProps(link);

    return (
        <LinkWrapperIfHref
            link={linkProps}
            className={cn(styles.imageLink, { [styles.hoverLink]: !!link?.href })}
            dataCy="molecules-content-sampling-image-link"
        >
            <div className={styles.contentWrapper}>
                {image?.mobile?.source && (
                    <ImageWrapper
                        src={image.mobile.source}
                        alt={image?.mobile?.altText || ''}
                        optimizedHeight={image?.mobile?.height}
                        optimizedWidth={image?.mobile?.width}
                        maxWidth={400}
                        className={cn(styles.imageStyles, styles.hiddenDesktop)}
                    />
                )}

                {image?.desktop?.source && (
                    <ImageWrapper
                        src={image.desktop.source}
                        alt={image?.desktop?.altText || ''}
                        optimizedHeight={image?.desktop?.height}
                        optimizedWidth={image?.desktop?.width}
                        maxWidth={600}
                        className={cn(
                            styles.imageStyles,
                            // Hide desktop on mobile ONLY if we have a mobile image to show instead - recreating Media with Text behavior
                            image?.mobile?.source && styles.hiddenMobile,
                        )}
                    />
                )}
            </div>

            {!!link?.href && <div className={styles.hoverForeground} />}
        </LinkWrapperIfHref>
    );
}

export default function ContentSampling({ sections, isReversed = false }: Props): React.JSX.Element {
    const [activeIndex, setActiveIndex] = useState(-1);

    const pingPongProps: ColumnProps = {
        media: { col: { sm: 12, lg: 7 } },
        text: { col: { sm: 12, lg: 5 } },
    };

    function getGridProps(i: number, columnType: ColumnType): GridProps {
        const base = pingPongProps[columnType];
        return {
            ...base,
            order: { lg: calculateOrder(isReversed, i, columnType === 'media') },
        };
    }

    return (
        <>
            {sections.map((section, sectionIndex) => {
                const sectionStyle: CSSProperties = section.desktopBackgroundUrl
                    ? ({
                          '--desktop-background': `url(${section.desktopBackgroundUrl})`,
                          '--mobile-background': `url(${section.mobileBackgroundUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                      } as CSSProperties)
                    : { backgroundColor: section.backgroundColor };

                const gridClassName = cn(styles.container, styles.pingPong, {
                    [styles.reversed]: isReversed,
                    [styles.lightTheme]: !!section.isLightTheme,
                });

                return (
                    <div key={sectionIndex} className={styles.section} style={sectionStyle}>
                        {section.items.map((item, itemIndex) => {
                            const globalIndex = sectionIndex * 1000 + itemIndex;

                            const headlineNode =
                                item.headline &&
                                (typeof item.headline === 'string' ? (
                                    <Text tag="span" variant="h4" className={styles.headlineText}>
                                        <SafeHtml print={item.headline} />
                                    </Text>
                                ) : (
                                    <div className={styles.headlineAlt}>{item.headline}</div>
                                ));

                            const headlineLinkProps = toLinkProps(item.link);

                            const modal: SamplingModalProps | undefined = item.audio?.transcript
                                ? {
                                      modalBody: (
                                          <>
                                              {item.audio.transcriptHeadline && (
                                                  <Text variant="h5" tag="h5">
                                                      {item.audio.transcriptHeadline}
                                                  </Text>
                                              )}
                                              <Text tag="p" className={styles.modalBody}>
                                                  {item.audio.transcript}
                                              </Text>
                                          </>
                                      ),
                                  }
                                : undefined;

                            return (
                                <Grid
                                    key={globalIndex}
                                    container
                                    align="center"
                                    standardMoleculeSpacing
                                    className={gridClassName}
                                >
                                    <Grid {...getGridProps(itemIndex, 'media')}>
                                        <div
                                            className={styles.mediaElementSingle}
                                            data-componenttype="AssetGroup:ContentSampling_img"
                                        >
                                            {item.foregroundImage && (
                                                <MediaImage image={item.foregroundImage} link={item.link} />
                                            )}
                                        </div>
                                    </Grid>

                                    <Grid {...getGridProps(itemIndex, 'text')}>
                                        <div
                                            className={cn(styles.contentCard, styles.spacing, {
                                                [styles.contentCardEven]: isEven(itemIndex),
                                            })}
                                        >
                                            {headlineNode && (
                                                <LinkWrapperIfHref link={headlineLinkProps} className={styles.headline}>
                                                    {headlineNode}
                                                </LinkWrapperIfHref>
                                            )}

                                            {item.body && (
                                                <Text
                                                    tag="div"
                                                    className={styles.bodyText}
                                                    dataCy="molecules-content-sampling-body"
                                                >
                                                    {item.body}
                                                </Text>
                                            )}

                                            {item.audio?.source && (
                                                <ContentSamplingPlayer
                                                    source={item.audio.source}
                                                    modal={modal}
                                                    isLightTheme={!!section.isLightTheme}
                                                    isActive={activeIndex === globalIndex}
                                                    onActivate={() => setActiveIndex(globalIndex)}
                                                    name={typeof item.headline === 'string' ? item.headline : ''}
                                                />
                                            )}
                                        </div>
                                    </Grid>
                                </Grid>
                            );
                        })}
                    </div>
                );
            })}
        </>
    );
}
